import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/router';
import { useObserver } from 'mobx-react-lite';
import {
  ConnectionStatus,
  GqlListRetailerPaymentTypesStatusQuery,
  StatusTypes,
  useListRetailerPaymentTypesStatusQuery,
} from 'types/graphql';
import useDispensaryPaymentMethods from 'shared/hooks/use-dispensary-payment-methods';
import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import useUser from 'shared/hooks/use-user';
import { ApolloQueryResult } from 'apollo-client';
import { LOG_NAMESPACES, relinkBankAccount } from 'src/payments/constants';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { getLogger } from 'src/utils/datadog-logger';

import type { EnrollmentBankAccount } from 'src/state/dutchiePay-enrollment';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

export type WhiteLabelConfig = {
  useWhiteLabel: boolean;
  brandedDutchiePayName: string | undefined;
  brandedDutchiePayLogoLight: string | undefined;
  brandedDutchiePayLogoDark: string | undefined;
};

export type UseDutchiePayUtils = {
  aggregator: string | undefined;
  allowDutchiePayEnrollment: boolean;
  bankAccounts: EnrollmentBankAccount[];
  connectionStatus: string | undefined;
  enrollmentStatusPending: boolean;
  hasEnrollmentData: boolean;
  isDutchiePayEnabledForDispo: boolean;
  isEnrolledDutchiePay: boolean;
  paymentMethodId: string | undefined;
  refetchRetailerPaymentData: () => Promise<ApolloQueryResult<GqlListRetailerPaymentTypesStatusQuery>>;
  shouldIncludePaymentMethodId: boolean;
  whiteLabelConfig: WhiteLabelConfig;
  disablePBBSignupForCuraleafPlus: boolean;
  activeBankAccount?: EnrollmentBankAccount;
  needsRelinkBankAccount: boolean;
};

export const useDutchiePay = (): UseDutchiePayUtils => {
  // Hooks
  const User = useUser();
  const router = useRouter();
  const { variant, hideLogin, isPlus } = useUI();
  const Cart = useCart();
  const flags = useFlags();
  const paymentOptions = useDispensaryPaymentMethods();
  const enrollmentStatus = useObserver(() => User.payByBankEnrollmentStatus);
  const bankAccounts = useObserver(() => User.payByBankBankAccounts);

  // Flags
  const internalSwitchesEnabled = flags['fintech.payment-processing.use-internal-feature-switches'];
  // Curaleaf is using a plus checkout within the context of their native mobile app which is preventing sso login due to the SSO login
  // not launching. They plan to move off of plus checkout and onto our white label mobile app but we need this flag in place until that occurs.
  // Once they move off of plus checkout, we can remove this variable and its references as they are only relevant to curaleaf plus.
  const disablePBBSignupForCuraleafPlus =
    flags['fintech.platform.disable-pbb-for-curaleaf-plus.rollout'] && isPlus && !!hideLogin;

  const { dispensary } = Cart.order;
  const { brandedDutchiePayLogoDark, brandedDutchiePayLogoLight, brandedDutchiePayName } = dispensary || {};
  const { plus, embedded } = InterfaceVariants;

  const hasWhiteLabelAssets =
    !!dispensary?.brandedDutchiePayName &&
    !!dispensary?.brandedDutchiePayLogoLight &&
    !!dispensary?.brandedDutchiePayLogoDark;

  const isWhiteLabeled = Boolean(
    dispensary?.brandedDutchiePayEnabled && hasWhiteLabelAssets && [embedded, plus].includes(variant)
  );

  const whiteLabelConfig = {
    useWhiteLabel: isWhiteLabeled,
    brandedDutchiePayName: isWhiteLabeled ? brandedDutchiePayName : '',
    brandedDutchiePayLogoLight: isWhiteLabeled ? brandedDutchiePayLogoLight : '',
    brandedDutchiePayLogoDark: isWhiteLabeled ? brandedDutchiePayLogoDark : '',
  };

  const {
    data: retailerPaymentData,
    error: retailerPaymentError,
    refetch: refetchRetailerPaymentData,
  } = useListRetailerPaymentTypesStatusQuery({
    variables: { ecommLocationId: dispensary?.id },
    skip: !internalSwitchesEnabled || !dispensary?.id,
  });

  if (retailerPaymentError) {
    logger.error('Error fetching retailer payment data', {
      error: retailerPaymentError,
      userId: User?.id ?? 'unknown',
      dispensaryId: dispensary?.id ?? 'unknown',
    });
  }

  const retailerIsEnabled = useMemo(
    () =>
      (retailerPaymentData?.listRetailerPaymentTypesStatus?.paymentTypes ?? []).some(
        (pmtType) => pmtType?.type === 'PAYMENT_TYPE_DUTCHIE_PAY' && pmtType.enabled === true
      ),
    [retailerPaymentData]
  );

  const activeBankAccount = useMemo(() => bankAccounts?.find((acct) => acct?.status === 'active'), [bankAccounts]);

  const paymentMethodId = activeBankAccount?.id;

  const isEnrolledDutchiePay = enrollmentStatus === StatusTypes.statusEnrolled && !!activeBankAccount;

  const enrollmentStatusPending = enrollmentStatus === StatusTypes.statusAccountPending;

  const aggregator = activeBankAccount?.aggregator;

  // Current dispo settings
  const payByBankActive = paymentOptions?.some(({ value, disabled }) => value === 'dutchiePay' && !disabled);

  const isDutchiePayEnabledForDispo = useMemo(
    () =>
      internalSwitchesEnabled
        ? retailerIsEnabled && !!payByBankActive && !disablePBBSignupForCuraleafPlus
        : !!payByBankActive && !disablePBBSignupForCuraleafPlus,
    [internalSwitchesEnabled, retailerIsEnabled, payByBankActive, disablePBBSignupForCuraleafPlus]
  );

  const allowDutchiePayEnrollment = isDutchiePayEnabledForDispo && !isEnrolledDutchiePay;
  const connectionStatus = activeBankAccount?.connectionStatus;

  const needsRelinkBankAccount = useMemo(
    () =>
      connectionStatus === ConnectionStatus.connectionStatusPendingExpiration ||
      connectionStatus === ConnectionStatus.connectionStatusError,
    [connectionStatus]
  );

  const relinkBankAccountEnabled = router.query.step === relinkBankAccount;
  const shouldIncludePaymentMethodId = Boolean(paymentMethodId && relinkBankAccountEnabled && needsRelinkBankAccount);

  return {
    activeBankAccount,
    aggregator,
    allowDutchiePayEnrollment,
    bankAccounts,
    connectionStatus,
    enrollmentStatusPending,
    hasEnrollmentData: !!enrollmentStatus && !!activeBankAccount,
    isDutchiePayEnabledForDispo,
    isEnrolledDutchiePay,
    paymentMethodId,
    refetchRetailerPaymentData,
    shouldIncludePaymentMethodId,
    whiteLabelConfig,
    disablePBBSignupForCuraleafPlus,
    needsRelinkBankAccount,
  };
};
